import React from 'react';
import PropTypes from 'prop-types';
import { formatDateTime, relativeTime } from '../../bsa/projects/projectListModel';
import { TENANT_SETTING_META } from '../registry';
import { Card, ItemControl, ItemPills, SettingRow } from '../SuperAdminAtoms';

export const groupBy = (items, key) => {
  const out = new Map();
  items.forEach(i => {
    if (!out.has(i[key])) out.set(i[key], []);
    out.get(i[key]).push(i);
  });
  return [...out.entries()];
};

// One registry item as a PlatformSettings SettingRow.
export const ItemRow = ({ item, highlight }) => (
  <SettingRow id={`row-${item.uid}`} label={item.label} hint={item.description} highlight={highlight}>
    <div className="bz-row" style={{ gap: 8, flexWrap: 'wrap' }}>
      <ItemControl item={item} withLabel />
      <ItemPills item={item} />
    </div>
    <div className="bz-muted" style={{ fontSize: 'var(--fs-xs)', marginTop: 8 }}>
      <span className="bz-mono">{item.keyLabel}</span>
      {item.updatedAt && <> · Updated {relativeTime(item.updatedAt)}</>}
      {item.kind === 'action' && (
        <>
          {item.schedule && <> · {item.schedule}</>} ·{' '}
          <span title={item.lastRunAt ? formatDateTime(item.lastRunAt) : undefined}>
            Last run {item.lastRunAt ? relativeTime(item.lastRunAt) : 'never'}
          </span>
        </>
      )}
    </div>
  </SettingRow>
);
ItemRow.propTypes = { item: PropTypes.object.isRequired, highlight: PropTypes.bool };

// tenantSettings rows read in the registry's order. A product or feature row the
// API never stored is synthesised after the stored ones, so without this it would
// land at the bottom of its card. Rows the registry doesn't know keep API order, last.
const META_ORDER = Object.keys(TENANT_SETTING_META);
const ROW_SOURCES = new Set(['product', 'feature', 'tenantSetting']);
const rank = item => {
  const at = ROW_SOURCES.has(item.source) ? META_ORDER.indexOf(item.id) : -1;
  return at < 0 ? META_ORDER.length : at;
};
const inRegistryOrder = items => [...items].sort((a, b) => rank(a) - rank(b)); // sort is stable

// A section whose controls are plain rows, one card per group.
const GroupedRows = ({ items, focusUid }) =>
  items.length ? (
    groupBy(inRegistryOrder(items), 'group').map(([group, rows]) => (
      <Card key={group} title={group}>
        {rows.map(i => (
          <ItemRow key={i.uid} item={i} highlight={focusUid === i.uid} />
        ))}
      </Card>
    ))
  ) : (
    <div className="pl-empty">
      <div className="pl-empty__title">No settings here</div>
      <div className="bz-muted">This tenant has no rows for this section yet.</div>
    </div>
  );
GroupedRows.propTypes = { items: PropTypes.array.isRequired, focusUid: PropTypes.string };

export default GroupedRows;
